import lang from 'dojo/_base/lang';
import { compose, mdsWidget, mdsLazyProto } from 'dojo/_base/declare';
import _WidgetBaseSP from 'more/_WidgetBaseSP';
import on from 'dojo/on';
import dojoQuery from 'dojo/query';
import _StatefulBase from '../_StatefulBase/_StatefulBase';
import _WidgetBase from 'dijit/_WidgetBase';

/**
 * Interface für das Widget das die Vererbungslinie angibt um die korrekten Typings zu setzen
 */
type _StatefulSection__mdsbase = InstanceType<typeof _WidgetBaseSP>;
interface _StatefulSection extends _StatefulSection__mdsbase { }

interface _StatefulFormdata {
    changedValues: Array<{
        inputname: string,
        value: any
    }>
}

interface RegisteredInput {
    input: any,
    node: HTMLElement,
    dataKey: string,
    format?: string,
    mandatory?: boolean,
    isComplexAttribute: boolean,
    _changeHandle?: any,
    initialReadonlyState: boolean,
    type: "?" | "htmlInput" | "statefulSection" | "statefulBase" | "static"  | "multilang"
}

interface RegisteredMultilangInput extends RegisteredInput{
    multilangDatakey: string
}

interface SetDataOptions { bSuppressResetDirty: boolean }

type StartupState = "loading" | "ready" | "disabled";
type _StartupStateInternal = StartupState | "initial" | "beforeReady";
export default function _StatefulSection(__Base: any) {
  const __C = 
class _StatefulSection extends __Base {


    protected formData: _StatefulFormdata;
    private dirty: boolean;
    private valid: boolean;
    private registeredConditionalNodes: Array<{
        node: HTMLElement,
        dataKey: string,
        dataValue?: string,
        reverse: boolean
        action: "showIfValue" | "hideIfEmpty"
    }>;
    private registeredPrivilegeNodes: Array<{
        element: HTMLElement | HTMLInputElement | HTMLTextAreaElement | _StatefulSection | MdsLax<InstanceType<typeof _WidgetBase>>,
        privilegeVisible: string,
        privilegeWriteable: string
    }>;
    protected registeredInputs: Array<RegisteredInput>;
    protected registeredWidgets: Array<MdsLax<InstanceType<typeof _WidgetBase>>>;
    private registeredSectionNames: Array<{
        name: string,
        element: HTMLElement | MdsLax<InstanceType<typeof _WidgetBase>>
    }>;

    protected sourceData: any;
    protected currentData: any;
    protected isDataDeleted: boolean;
    protected changedData: Object;
    private _undirtyData: string;
    private _readOnlySection: boolean | "initial";
    protected _isSectionAvailable: boolean; // verfügbar über Rechte und Features

    protected entityId: string;
    private displayname: string;

    private forceCheckDirty: boolean;

    displaynameProperty: string;
    valueProperty: string;

    protected startupState: _StartupStateInternal;

    forceSetDirty(isDirty: boolean): void {
        this.dirty = isDirty;
        this.emit("dirty", isDirty);
        this.onDirtyChange(isDirty);
    }

    setData(data: Object, valueproperty?: string, displaynameProperty?: string,
        options?: SetDataOptions) {

        if (!options?.bSuppressResetDirty || !this.sourceData) this.sourceData = data;

        try {
            if (!valueproperty) valueproperty = this.valueProperty ? this.valueProperty : "ID";
            this.valueProperty = valueproperty;

            if (data && valueproperty && data.hasOwnProperty(valueproperty)) {
                if (data[valueproperty]) this.entityId = data[valueproperty].toString();
            }


            if (!displaynameProperty) displaynameProperty = this.displaynameProperty ? this.displaynameProperty : "$Displayname";
            this.displaynameProperty = displaynameProperty;

            if (data && displaynameProperty && data.hasOwnProperty(displaynameProperty)) {
                if (data[displaynameProperty]) this.displayname = data[displaynameProperty].toString();
            }
        } catch (ex) {

        }
        //console.debug(this.id + " setData", data, "startup state before setData: " + this.startupState);

        if (!options?.bSuppressResetDirty) {
            this.startupState = "loading";
        }

        this.dataKeys = [];
        this.dataValues = [];

        if (typeof (data) == "object") {

            // Spezial: Keyword ___root als data-key eintragen, damit das gesamt Objekt auf sich selber appliziert wird
            this.dataKeys.push("___root");
            this.dataValues.push(data);

            this._parseDataPaths(data);


            //console.debug(this.id + " setData _parseDataPaths", data, this.dataKeys, this.dataValues);
        }

        this.currentData = lang.clone(data); // currentdata soll den aktuellen stand erhalten
        if (options?.bSuppressResetDirty) { // auch aus den changedData alle props übertragen
            lang.mixin(this.currentData, this.changedData);
        }

        this.emit("dataSet", this.currentData);


        this.fillTemplate();

        // dieses Timeout ist blursed:
        // wir brauchen ein Timeout, weil sonst beim ersten Datensetzen (im filltemplate) das ready und reset kommen, bevor alle Inputs ihren initialen Wert zurückgemeldet haben
        // aber dadurch kommts auch im Afterready zu unschönen Situationen, wenn ein Input einen Wert erhält, aber das Timeout mit dem Reset noch gar nicht durch ist und somit die changedData wieder auslöscht
        if (this._finishSetDataTimeout) clearTimeout(this._finishSetDataTimeout);
        this._finishSetDataTimeout = setTimeout(lang.hitch(this, this._finishSetData, options), 220);
    }



    private _finishSetDataTimeout;
    private _finishSetData(options: SetDataOptions, waitedMs: number = 0) {
        this._finishSetDataTimeout = null;
        if (this["_destroyed"] === true) return; // Section inzwischen zerstört (Tab/Dialog zu) — nichts mehr finalisieren

        // Race-Guard: Inputs mit noch laufendem initialen Listen-Load haben ihren Wert noch nicht
        // zurückgemeldet. Jetzt resetten/ready würde deren Echo als dirty werten (bzw. Werte
        // verlieren), wenn das Entity-Load schneller war als die Listen-Endpoints.
        if (waitedMs < 5000 && this._hasPendingInitialListLoads()) {
            this._finishSetDataTimeout = setTimeout(
                lang.hitch(this, this._finishSetData, options, waitedMs + 100), 100);
            return;
        }
        if (waitedMs >= 5000) console.warn(this.id + " _finishSetData: pending list loads after 5s, proceeding anyway");

        this.startupState = "beforeReady";
        // Standardmäßig wird nach dem Setdata immer ein Reset ausgeführt, außer die Options sagen nein:
        if (!options?.bSuppressResetDirty) this.resetDirty();

        // RL 2026-05-06: Reihenfolge Reset und Ready umgedreht. damit afterready erst nach dem Reset kommt

        this.setStartupState("ready");
    }

    private _hasPendingInitialListLoads(): boolean {
        for (let regInput of this.registeredInputs) {
            let input: any = regInput.input;
            if (!input) continue;
            if (typeof input.isInitialListLoadPending == "function" && input.isInitialListLoadPending()) return true;
            if (input instanceof _StatefulSection && input._hasPendingInitialListLoads()) return true; // verschachtelte Sections (___root)
        }
        return false;
    }

    isAvailable(): boolean{
        return this._isSectionAvailable;
    }


    protected afterSetData() {
        // für Anwendung in abgeleiteten Widgets, zb. Formatierung aufgrund von Inhalten
    }

    protected afterReady() {
        // für Anwendung in abgeleiteten Widgets, zb. Formatierung aufgrund von Inhalten
    }

    /**
     *
     * */
    setStartupState(state: StartupState) {
        // umwandeln in den internen Startupstate:
        let _state: _StartupStateInternal = state;
        if (!_state) _state = "initial";
        let oldState = this.startupState;

        //console.trace(this.id + " setStartupState " + oldState + " => " + state, this.registeredInputs, lang.clone(this.changedData));

        this.startupState = _state;

        for (let regInput of this.registeredInputs) {
            if (regInput.input instanceof _StatefulSection) {
                if (regInput.input.startupState != _state) {
                    regInput.input.setStartupState(_state as any); // übersteuern - hier dürfen wir das
                }
            }
        }

        if (this.startupState == "ready" && oldState != "ready") {
            if( this.__isDojoStartupDone != true) this.startup();
            this.emit("ready");
            this.afterReady();
        }
    }

    protected onDirtyChange(newState: boolean) {
        // für abgeleitete Widgets
    }


    private _onChanged_private(attribute: string, newValue: any) {
        try {
            if (attribute == this.displaynameProperty) this.displayname = newValue.toString();
        } catch (ex) {

        }

        if (this.startupState == "ready") {
            this.checkVisible();

            this.emit("change", { attribute: attribute, value: newValue, changedData: this.changedData });
        }


        this._onChanged(attribute, newValue);

       /* console.log(this.id + "onchanged", attribute, newValue, this.changedData, this.currentData);*/
    }
    protected _onChanged(attribute: string, newValue: any) {
        // für Anwendung in abgeleiteten Widgets, zb. Formatierung aufgrund von Inhalten
    }

    /**
     * Fügt einen onchanged-Reaktor hinzu, der auf die Änderung eines Attributpfades reagiert und ein Callback auslöst
     * @param attribute Attributpfad, der überwacht wird
     * @param callback Wird aufgerufen mit diesen Parameter: Attributpfad, neuer Wert, alter Wert
     */
    addOnchanged(attribute: string, callback: Function) {
        if (attribute && typeof (callback) == "function") {
            this._onChangedRegistry.push({
                attribute: attribute, callback: callback
            });
        }
    }

    checkMandatoryInputs(): boolean {
        for (let regInput of this.registeredInputs) {
            if (regInput.input instanceof _StatefulBase) {
                if (!regInput.input.validateInput()) {
                    //debugger;
                    console.log("checkMandatoryInputs", regInput, this.id);
                    return false;
                }
            } else if (regInput.input instanceof _StatefulSection) {
                if (!regInput.input.checkMandatoryInputs()) {
                    //debugger;
                    console.log("checkMandatoryInputs", regInput, this.id);
                    return false;
                }
            } else if (regInput.input instanceof HTMLInputElement ||
                regInput.input instanceof HTMLSelectElement ||
                regInput.input instanceof HTMLTextAreaElement) {
                if (regInput.mandatory === true && !regInput.input.value) {
                    console.log("checkMandatoryInputs", regInput, this.id);
                    return false;
                }
            } else if (regInput.input == null && regInput.node instanceof HTMLElement) {

            }
        }
        return true;
    }

    private _onChangedRegistry: Array<{
        attribute: string,
        callback: Function
    }>;

    /**
     * Ruft weitere Reaktionen auf einer vererbten Statefulsection auf.
     */
    protected onSetReadOnly(isReadonly: boolean) {

    }

    isReadonly(): boolean {
        return this._readOnlySection === true;
    }

    setReadonly(readonly: boolean | "initial" = true) {
        if (readonly == null) readonly = "initial";
        this._readOnlySection = readonly;

        for (let regInput of this.registeredInputs) {

            let actualValue = this._readOnlySection == "initial" ? regInput.initialReadonlyState : this._readOnlySection;

            if (regInput.input instanceof _StatefulBase) {
                regInput.input.setReadonly(actualValue);
            } else if (regInput.input instanceof _StatefulSection) {
                if (this._readOnlySection == "initial") actualValue = null;
                regInput.input.setReadonly(this._readOnlySection); // statefulsection kann auch "initial" als Wert bekommen
            } else if (regInput.input instanceof HTMLInputElement || regInput.input instanceof HTMLSelectElement || regInput.input instanceof HTMLTextAreaElement) {
                //this.UiModder.domClass.toggle(regInput.input, "w3-disabled", actualValue);
                regInput.input.disabled = actualValue;
            } else if (regInput.input == null && regInput.node instanceof HTMLElement) {
                // nix passiert. sind einfach
            }
        }

        this.onSetReadOnly(this._readOnlySection === true);
    }

    protected setWidgetReadonly(attribute: string, readonly: boolean) {
        let widgets = this.getRegisteredWidgetsByDataKey(attribute);
        widgets.forEach((widget: MdsLax<InstanceType<typeof _StatefulBase>>) => widget.setReadonly(readonly));

    }

    protected getRegisteredWidgetsByDataKey(key: string) {
        let widgets = [];
        for (let widget of this.registeredWidgets) {
            if (widget.domNode.dataset["key"] == key) {
                widgets.push(widget);
            }
        }

        return widgets;
    }

    resetDirty() {
        this._undirtyData = JSON.stringify(this.currentData);
        this.changedData = {};
        let isDirty = false;
        if (this.dirty != isDirty) {
            //console.debug(this.id + " dirty reset", this.startupState);
            this.dirty = isDirty;
            if (this.startupState == "ready" || this.startupState == "beforeReady") {
                this.emit("dirty", this.dirty);
                this.onDirtyChange(this.dirty);
            }
        }
    }

    setForceCheckDirty(forceCheckDirty: boolean = true) {
        this.forceCheckDirty = forceCheckDirty
    }


    private _checkDirty(forceCheck: boolean = false) {

        let isDirty = false;
        if (this._readOnlySection == true && !this.forceCheckDirty) {

        } else {
            let strData = JSON.stringify(this.currentData);
            isDirty = this._undirtyData != strData;
        }


        if (this.dirty != isDirty) {
            this.dirty = isDirty;

            if (this.startupState == "ready") {
                this.emit("dirty", this.dirty);
                this.onDirtyChange(this.dirty);
            }
        }

        //console.debug(this.id + " checkDirty", isDirty, this.changedData, this.currentData);
    }

    private checkVisible() {
        for (let condition of this.registeredConditionalNodes) {
            let value = this.getAttributeValue(condition.dataKey);

            let strValue = "null";
            if (typeof value != "undefined" && value != null) {
                strValue = value.toString();
            }
            let result = value == null
                || typeof value == "undefined"
                || value === false
                || value == "";

            if (condition.dataValue) {
                result = strValue == condition.dataValue;
            }

            if (condition.reverse == true) result = !result;

            if (condition.action == "hideIfEmpty") {
                //console.debug(this.id, "hide element because empty?", result, condition, value);
                this.hideNode(condition.node, result);

            } else if (condition.action == "showIfValue") {
                //console.debug(this.id, "show element because value?", !result, condition, value);
                this.hideNode(condition.node, !result);
            }
        }
    }

    getAttributeValue<expectedType = any>(attribute: string ): expectedType {
        for (let i = 0; i < this.dataKeys.length; i++) {
            let dataKey = this.dataKeys[i];
            if (dataKey == attribute) {
                return this.dataValues[i];
            }
        }
        return null;
    }

    private fillTemplate() {
        if (this.startupState != "ready") {
            this.startupState = "loading";
        }

        for (let i = 0; i < this.dataKeys.length; i++) {
            let dataKey = this.dataKeys[i];
            let value = this.dataValues[i];


            for (let regInput of this.registeredInputs) {
                if (regInput.dataKey == dataKey) {
                    let targetelement = regInput.input;
                    if (regInput.type == "multilang") {
                        // Extrawurst für multilangEdits: da kommt außer dem "einfachen" datakey noch ein ganzes Paket mit Übersetzungen!
                        let multilangValue = {
                            entity: this.sourceData, targetAttribute: dataKey
                        }

                        targetelement.setValue(multilangValue, true);

                    }

                    this._setValueToUI(regInput, value);
                }
            }
        }

        this.checkVisible();

        this.afterSetData();
    }

    private _setValueToUI(registeredInput: RegisteredInput, value: any) {
        if (registeredInput.format) {
            try {
                if (typeof value == "number") {
                    //// TODO: Formatierung für Nummern....
                    //let nString = dojoNumber.format(number, {
                    //    type: this.numberType,
                    //    places: this.places,
                    //    locale: this.locale,
                    //    fractional: /*this.decimals > 0 || */this.fractional
                    //});

                } else if (typeof value == "string") {
                    let date = new Date(value);
                    value = this.dateToString(date, registeredInput.format);
                }
            } catch (ex) {

            }
        }


        let targetelement = registeredInput.input;

        if (targetelement instanceof _StatefulBase) {



            if (registeredInput.type == "multilang") {
                // extra extra.... ein neuer Wert kann string sein und einfach auf die "current"-Sprache
                // kann aber auch ein Objekt sein und alle Sprachen enthalten etc
            }


            targetelement.setValue(value, this.startupState != "ready");

        } else if (targetelement instanceof _StatefulSection) {
            console.debug("_setValueToUI on nested section: " + this.id + " (" + this.startupState + ") => " + targetelement.id + " (" + targetelement.startupState + ")", value);

            if (typeof (value) == "object" && value != null) {
                //targetelement.setData(lang.clone(value)); // setData ersetzt alles und macht die Section ready. kann zu unschönen race conditions führen
                //targetelement.sourceData = lang.clone(this.sourceData);
                //targetelement.setValueToDataKey(targetelement.currentData, "___root", value);
                let changedData = targetelement.changedData;
                //targetelement.on("ready", lang.hitch(this, this.applyChangedData, targetelement, changedData));
                
                //debugger;
                targetelement.setData(lang.clone(value), null, null, { bSuppressResetDirty: (this.startupState == "ready") });
            } else {
                //console.warn(this.id + " _setValueToUI - nullvalue to statefulsection?", registeredInput);
                targetelement.setData(value);
                //targetelement.setValueToDataKey(targetelement.currentData, "___root", value);
            }
        } else if (targetelement instanceof HTMLInputElement) {
            targetelement.value = value;
        } else if (targetelement instanceof HTMLSelectElement) {
            targetelement.value = value;
        } else if (targetelement instanceof HTMLTextAreaElement) {
            targetelement.value = value;
        } else if (targetelement == null && registeredInput.node instanceof HTMLElement) {
            this.UiModder.domAttr.set(registeredInput.node, "innerHTML", value);
        }
    }

    private dataKeys: Array<string>;
    private dataValues: Array<any>;

    private _parseDataPaths(data: Object, currentPath: Array<string> = []) {
        for (let prop in data) {
            currentPath.push(prop);

            if (data[prop] instanceof Object) {

                let path = currentPath.join(".");
                let existingIndex = this.getDataPathIndexForDataKey(path);
                if (existingIndex == -1) {
                    this.dataKeys.push(path);
                    this.dataValues.push(data[prop]);
                } else {
                    this.dataValues[existingIndex] = data[prop];
                }


                // rekursiv rein in das Objekt:
                if (lang.isArray(data[prop])) {
                    // wenn Array, was dann?
                } else {
                    this._parseDataPaths(data[prop], currentPath);
                }

            } else {
                let path = currentPath.join(".");
                let existingIndex = this.getDataPathIndexForDataKey(path);
                if (existingIndex == -1) {
                    this.dataKeys.push(path);
                    this.dataValues.push(data[prop]);
                } else {
                    this.dataValues[existingIndex] = data[prop];
                }
            }

            currentPath.pop();
        }

    }
    /**
     * * Gibt das eigentliche, nicht komplexe Attribut in einem datakey-Pfad zurück. (zB. ref_x.ref_y.attr -> attr)
     */
    private getNonComplexAttributeOfDataKey(dataKey: string): string {
        dataKey = dataKey.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
        dataKey = dataKey.replace(/^\./, '');           // strip a leading dot

        let pathParts = dataKey.split('.');

        let lastElement = pathParts.pop();

        return lastElement;
    }
    /**
     * Gibt den Pfad BIS zum tatsächlichen Attribut zurück. (zB. ref_x.ref_y.attr -> ref_x.ref_y)
     */
    private getComplexAttributePathToDataKey(dataKey: string): string {
        dataKey = dataKey.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
        dataKey = dataKey.replace(/^\./, '');           // strip a leading dot

        let pathParts = dataKey.split('.');

        let lastElement = pathParts.pop();

        if (pathParts.length > 1) return pathParts.join(".");
        else if (pathParts.length == 1) return pathParts[0];

        return "";
    }
    /**
     * Ermittelt das Objekt innerhalb von data, das das gesuchte (ggf. komplexe!) Property 'dataKey' enthält, und gibt es zurück
     * ACHTUNG: Gibt nicht das TATSÄCHLICHE dataKey zurück!
     * @param data
     * @param dataKey
     * @returns
     */
    private getContainingObjectOfDataKey(data: Object, dataKey: string) {
        dataKey = dataKey.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
        dataKey = dataKey.replace(/^\./, '');           // strip a leading dot
        let pathParts = dataKey.split('.');
        if (pathParts.length == 1) return data;

        for (let i = 0; i < pathParts.length; i++) {
            let pathPart = pathParts[i];
            if (data != null && pathPart in data) {
                // Objekte sind der neue Ausgangspunkt - wenn es nicht schon der letzte Teil des Pfades ist!
                if (data[pathPart] instanceof Object && (i < pathParts.length - 1)) {
                    // aber Achtung. es könnte auch ein Date sein!!
                    if (data[pathPart] instanceof Date && typeof(data[pathPart]["getMonth"]) == "function") {
                        return data;
                    }
                    data = data[pathPart];
                } else {
                    return data;
                }
            } else {
                return null;
            }
        }
        return data;
    }

    getCurrentData(): any {
        return this.currentData;
    }

    getChangedData(): any {
        return this.changedData;
    }

    getEntityId(): string {
        return this.entityId;
    }
    getDisplayname(): string {
        return this.displayname;
    }
    /**
    * Liefert den aktuellen Dirty-Zustand (Alias für IsDirty)
    * @param forceUpdate Wenn true, wird nochmal eine Neuberechnung ausgelöst. Ansonsten liefert die Funktion den zuletzt berechneten Wert.
    */
    checkDirty(forceUpdate?: boolean): boolean {
        return this.isDirty(forceUpdate);
    }
    /**
    * Liefert den aktuellen Dirty-Zustand.
    * @param forceCheck Wenn true, wird nochmal eine Neuberechnung ausgelöst. Ansonsten liefert die Funktion den zuletzt berechneten Wert.
    */
    isDirty(forceCheck?: boolean): boolean {

        if (forceCheck === true) this._checkDirty();

        if (this._readOnlySection == true) return false;

        return this.dirty;
    }

    isValid(): boolean {
        return this.valid;
    }

    rollback(): void {
        if (!this.sourceData) return;

        //console.debug(this.id + " rollback", this.currentData, this.sourceData);

        this.formData = {
            changedValues: []
        };
        this.setStartupState("loading");
        this.setData(this.sourceData);
        this.setStartupState("ready");
        this.emit("dirty", false);
        this.onDirtyChange(false); // weil das reset im setData erst wartet bis der startupstate nicht mehr "loading" ist, damit es beim normalen
        // Laden nicht zu false positives kommt...
    }

    //addFormInput(input: _StatefulBase | _StatefulSection) {

    //}

    private getRegisteredInputIndexForDataKey(dataKey: string): Array<number> {
        let results = [];
        for (let i = 0; i < this.registeredInputs.length; i++) {
            let reg = this.registeredInputs[i];

            if (reg.dataKey == dataKey) {
                results.push(i);
            }
            else if (reg.type == "multilang" && (reg as RegisteredMultilangInput).multilangDatakey == dataKey) {
                results.push(i);
            }
        }

        return results;
    }

     private getDataPathIndexForDataKey(dataKey: string): number {
        for (let i = 0; i < this.dataKeys.length; i++) {
            if (this.dataKeys[i] == dataKey) {
                return i;
            }
        }

        return -1;
    }
    /**
     * Erweitert ein Objekt um einen komplexen Pfad. Rcückgabe: das innerste Objekt des Pfades.
     * @param sourceObject Objekt, das erweitert werden soll.
     * @param complexPath Pfad, der zerlegt wird. Wenn es ein mehrteiliger Attributpfad ist, werden dem SourceObject entsprechend verschachtelte Objektproperties hinzugefügt
     * @returns
     */
    private translateComplexPath(sourceObject: Object, complexPath: string): Object {
        if (!complexPath) return null;
        if (!sourceObject) sourceObject = {};

        let pathParts = complexPath.split(".");
        let firstPart = pathParts.shift();
        if (pathParts.length == 0) {
            sourceObject[complexPath] = null;
            return sourceObject;
        }
        else {
            if (!sourceObject[firstPart]) {
                sourceObject[firstPart] = {};
            } else if (typeof (sourceObject[firstPart]) != "object") {
                // ref_x.ref_y.bla ist gefragt
                // dann kommen wir drauf, dass es ref_y schon gibt, aber da steht zb ein String drauf
                // da stimmt also was nicht - wenn bla dazukäme, dann muss ref_y ein Object sein.
                return null;
            }
            let returnValue = this.translateComplexPath(sourceObject[firstPart], pathParts.join("."));
            return returnValue;
        }
    }
    /**
     * Öffentlich verfügbare Setfunktion, um einem Attributpfad und zugehörigem Input einen Wert zuzuweisen.
     * Löst dadurch die üblichen onchanged-Reaktoren und dirty-Emits aus.
     * Rückgabe: true, wenn eine Änderung des bisherigen Werts erkannt wurde.
     * @param attributepath Attributpfad, auf den der gewünschte Wert gesetzt wird. Falls das gesamt Datenobjekt ersetzt werden soll, '___root' angeben.
     * @param value Attributwert
     */
    setAttributeValue(attributepath: string, value: any, forceCreatePath: boolean = true): boolean {

        //console.debug(this.id + "setAttributeValue", attributepath, value, "startup state:" + this.startupState);
        if (!this.currentData) this.currentData = {};

        let pathIndex = this.getDataPathIndexForDataKey(attributepath);
        if (pathIndex == -1) {
            if (forceCreatePath != true) return false;
            // blöd, wenn hier ein legitimes Attribut gesetzt wird, das vorher (wegen neuem Datensatz!) nicht in die Datakeys geparst worden ist,
            // dann bekomme ich keinen Index und die Section würde keinen Pfad-Wert-Paar prüfen und setzen
            // ich muss die currentData also um diesen POTENZIELL KOMPLEXEN Attributpfad erweitern
            this.translateComplexPath(this.currentData, attributepath);
            this._parseDataPaths(this.currentData);
        }

        let result = this.setValueToDataKey(this.currentData, attributepath, value);

        //this._parseDataPaths(this.currentData);
        pathIndex = this.getDataPathIndexForDataKey(attributepath);

        //console.debug(this.id + " after setAttributeValue and parseData", attributepath, value, this.currentData, this.dataKeys, this.dataValues);

        if (pathIndex != -1) {
            for (let reg of this.registeredInputs) {
                // alle registrierten Elemente mit diesem Attributpfad ändern
                // oder alle root-Element nochmal komplett
                if (reg.dataKey == attributepath) {
                    this._setValueToUI(reg, value);
                } else if (reg.dataKey == "___root"
                    && result == true /* sinnlos potenziell ganz viele Elemente nochmal zu bespielen, wenn sich nix geändert hat */) {
                    this._setValueToUI(reg, this.currentData);
                }
            }
        }


        this._checkDirty();

        return result;
    }
    private setValueToDataKey(targetData: Object, dataKey: string, value: any): boolean {

        let valueChanged: boolean = false;
        let actualAttribute = this.getNonComplexAttributeOfDataKey(dataKey);

        let targetObject = this.getContainingObjectOfDataKey(targetData, dataKey);

        if (typeof (value) == "object" && value != null) {
            value = lang.clone(value); // Kopie machen, damit es keine byref-Schwierigkeiten gibt
        }

        //console.trace(this.id + " setValueToDataKey", dataKey, value, targetData );

        if (dataKey == "___root") {

            if (typeof (value) == "object" && value != null) {

                this.isDataDeleted = false;

                // das gesamte eigene Objekt ist adressiert, weil ein Statefulsection-Widget ein change mit einem Datenpaket ausgelöst hat
                // wir können hier nicht normal weitermachen, stattdessen setzen wir alle Eigenschaften einzeln:
                if (this.currentData == null) {
                    // falls es vorher gar keinen Wert gab (was legitim ist), dann ein neues Objekt erzeugen:
                    this.currentData = {};
                    this.changedData = {};
                }

                //console.debug(this.id + " setValueToDataKey ___root changing BEFORE", dataKey, value, this.currentData, this.dataKeys, this.dataValues);

                for (let key in value) {
                    valueChanged = this.setValueToDataKey(this.currentData, key, value[key]) || valueChanged;
                    //let inputIndex = this.getRegisteredInputIndexForDataKey(key);
                    //for (let index of inputIndex) {
                    //    if (index > -1) this._setValueToUI(this.registeredInputs[index], value[key]);
                    //}
                }

                this._parseDataPaths(this.currentData);

                let rootIndex = this.getDataPathIndexForDataKey("___root");
                if (rootIndex >= 0) this.dataValues[rootIndex] = this.currentData;

                //console.debug(this.id + " setValueToDataKey ___root changing AFTER", dataKey, value, this.currentData, this.dataKeys, this.dataValues);

                // falls das Objekt jetzt insgesamt verändert wurde: onchanged auslösen dezidiert für "mich als gesamtes":
                if (valueChanged) {
                    this._onChanged_private("___root", this.changedData);
                }


            } else if (value == null) {
                if (this.currentData != null) {
                    this.currentData = null;
                    this.isDataDeleted = true; // WICHTIG. Widgets die diese Section verwenden, können sonst nicht erkennen was los ist!
                    this.dataKeys = [];
                    this.dataValues = [];
                    this.changedData = {} // das ist alles resettet!

                    this._onChanged_private("___root", null);

                    valueChanged = true;
                }
            } else if (typeof (value) != "object") {
                console.error(this.id + ": cannot set a non-object-value to ___root, since ___root must always be a dataset. check returnvalue of widget!", value);
            }
            return valueChanged;
        }

        // Ausnahme: wenn value von einem DateTimeInput kommt, dann einfach nur das .date verwenden...
        if (value && value.date && value.timezone) {
            value = value.date;
        }

        if (!targetObject && actualAttribute) {
            targetObject = {};
            targetObject[actualAttribute] = null;
        }

        if (targetObject && actualAttribute/* && actualAttribute in targetObject*/) {
            let oldValue = targetObject[actualAttribute];
            let oldStrValue = oldValue;
            let strValue = value;
            try {
                if (typeof (targetObject[actualAttribute]) == "object") {
                    oldStrValue = JSON.stringify(oldStrValue);
                }
                if (typeof (value) == "object") {
                    strValue = JSON.stringify(value);
                }
            } catch (ex) {

            }

            targetObject[actualAttribute] = value;

            if (oldStrValue != strValue) {
                valueChanged = true;
                let index = this.getDataPathIndexForDataKey(dataKey);
                if (index >= 0) this.dataValues[index] = value;

                // unschön: bei komplexen Pfaden werden dann Properties "ref_x.ref_y.attribut" (flach!) hingeschrieben.
                // wir gehen noch davon aus, dass komplexe, tieferliegende Attribute sich nicht ändern (dürfen), sonden immer nur die oberste Ebene
                // wird uns irgendwann um die Ohren fliegen. Grüße aus der Vergangenheit!

                // hallo, hier spricht die Zukunft! dank MultilangEdit haben wir jetzt den Salat.
                // wir müssen komplexe Pfade umbauen zu richtigen Properties
                if (dataKey.indexOf(".") != -1) {
                    // zum Glück haben wir mittlerweile eh eine schöne Erweiterungsfunktion, die das für uns macht:
                    let targetObj = this.translateComplexPath(this.changedData, dataKey);
                    targetObj[this.getNonComplexAttributeOfDataKey(dataKey)] = value;

                    //    try {
                    //        let inputs = this.getRegisteredInputIndexForDataKey(dataKey);
                    //        if (inputs.length > 0) {
                    //            for (let index of inputs) {
                    //                let reg = this.registeredInputs[index];
                    //                if (reg.type = "multilang") {
                    //                    // bei MultiLang wurde ein ref_pfad.__multilang.attribut geändert. wir wollen aber ref_pfad.attribut
                    //                    dataKey = reg.dataKey;
                    //                }
                    //            }
                    //        }
                    //    } catch (ex) {
                    //    }
                } else {
                    this.changedData[dataKey] = value;

                }

                this._onChanged_private(dataKey, value);

                for (let entry of this._onChangedRegistry) {
                    if (entry.attribute == dataKey) {
                        entry.callback(dataKey, value, oldValue);
                    }
                }
            }
        }

        return valueChanged;
    }


    private _onChange_WidgetValue_NativeHTML(input: HTMLInputElement, register: RegisteredInput) {
        if (this.startupState != "ready") return;
        if (this._readOnlySection == true) return;

        if (!input.disabled) {
            this.setValueToDataKey(this.currentData, register.dataKey, input.value);
            this._checkDirty();
        }
    }

    private _onChange_WidgetValue_StatefulSection(widget: _StatefulSection, register: RegisteredInput, evtData) {


        if (register._changeHandle) {
            clearTimeout(register._changeHandle);
            register._changeHandle = null;
        }

        register._changeHandle = setTimeout(lang.hitch(this, this._delayed_onChange_WidgetValue_StatefulSection, widget, register, evtData), 50);
    }

    private _delayed_onChange_WidgetValue_StatefulSection(widget: _StatefulSection, register: RegisteredInput, evtData) {

        register._changeHandle = null;

        if (this.startupState != "ready") return;
        if (this._readOnlySection == true) return;

        if (widget._readOnlySection !== true) {

            // Achtung: die Section hat ein Datenobjekt, das vielleicht nicht mehr komplett mit "meinem" Datenobjekt ist
            // wir müssen also gezielt das holen, was von der Section geändert worden ist
            // sonst resetten wir Properties mit alten Werten, die sich wo anders vielleicht schon geändert haben

            let newData = lang.clone(this.currentData); 
            
            let target = null;
            if (register.dataKey == "___root") {
                target = newData;
            } else {
                let containingObj = this.getContainingObjectOfDataKey(newData, register.dataKey);
                target = containingObj[register.dataKey]
            }
            if (!target) {
                target = {};
            }

            if (widget.isDataDeleted) {
                // wenn das Widget zb nur aus einem einzigen Select oder Input besteht, dann kann auch "keine Auswahl" passiert sein
                // in dem Fall hat die Section keine Daten, aber auch keine changedData. sie muss kommunizieren, dass der neue Wert NULL ist
                target = null;
            } else {
                for (let property in widget.changedData) {
                    target[property] = widget.changedData[property];
                }
            }

            this.setValueToDataKey(this.currentData, register.dataKey, target);

            this._checkDirty();
        }
    }
    private _onChange_WidgetValue_StatefulBase(widget: MdsLax<InstanceType<typeof _StatefulBase>>, register: RegisteredInput, value?: any) {

        console.debug(this.id + "_onChange_WidgetValue_StatefulBase",
            "datakey: " + register.dataKey, register, value,
            "current state: " + this.startupState,
            "current changedata: ", this.changedData);

        if (this.startupState != "ready") {
            console.warn(this.id + " _onChange_WidgetValue_StatefulBase: not ready yet!", register, value);
            return;
        }
        if (this._readOnlySection == true) return;

        if (!widget.isReadonly) {
            switch (widget["declaredClass"]) {
                case "SearchSelect":
                    // Sonderregel für SearchSelect: muss das ganze Objekt zurückgeben
                    this.setValueToDataKey(this.currentData, register.dataKey, (widget as any).getValue(true));
                    break;

                case "MultiLangEdit":
                    // bei Multilang kennen wir zwar den Attributnamen, aber auf das Control muss ein Objekt
                    // mit speziellen Properties gesetzt werden und die Rückgabe ist auch mehrteilig

                    let multilangWidet = widget as any;
                    let fakeEntity = {};
                    multilangWidet.getValueOntoEntity(fakeEntity);

                    let currentLangValue: string = multilangWidet.getValueOfLanguage();

                    // setValueToDataKey funktioniert hier nicht einfach so. wir können erstmal nur den primitiven current-lang-string setzen:
                    this.setValueToDataKey(this.currentData, register.dataKey, currentLangValue)

                    // dann müssen wir aber auch noch die restlichen Sprachen draufsetzen:
                    // ref_x.description wird zu ref_x.__multilang.description
                    let attributePath = this.getComplexAttributePathToDataKey(register.dataKey);
                    let attribute = this.getNonComplexAttributeOfDataKey(register.dataKey);
                    let multilangPath = "__multilang";
                    if (attributePath) multilangPath = attributePath + "." + multilangPath;
                    multilangPath += "." + attribute;

                    let multilangvalue = multilangWidet.getValue(true);
                    this.setValueToDataKey(this.currentData, multilangPath, multilangvalue);
                    break;

                default:
                    this.setValueToDataKey(this.currentData, register.dataKey, widget.getValue());
                    break;
            }


            this._checkDirty();
        }
    }

    private _registerPrivilegedElements(): void {
        let nodesVis = dojoQuery("[data-privilege-visible]", this.domNode);
        let nodesWri = dojoQuery("[data-privilege-writeable]", this.domNode);
        //let nodesFeature = dojoQuery("[data-featurepath]", this.domNode);

        //for (let i = 0; i < nodesFeature.length; i++) {
        //    let entry = nodesFeature[i];
        //    let node: HTMLElement = null;
        //    if (entry instanceof HTMLElement) {
        //        node = entry;
        //    } else if (entry instanceof _WidgetBase) {
        //        node = entry.domNode;
        //    }
        //    if (node) {
        //        let neededFeature = node.attributes.getNamedItem("data-featurepath")?.value;


        //    }
        //}

        for (let widget of this.registeredWidgets) {

            let nodeIndexV = nodesVis.indexOf(widget.domNode);
            let nodeIndexW = nodesWri.indexOf(widget.domNode);

            if (nodeIndexV != -1 || nodeIndexW != -1) {
                if (nodeIndexV > -1) {
                    nodesVis.splice(nodeIndexV, 1);
                    nodeIndexW = nodesWri.indexOf(widget.domNode);
                }
                if( nodeIndexW > -1 ) nodesWri.splice(nodeIndexW, 1);

                let privVisible = widget.domNode.attributes.getNamedItem("data-privilege-visible")?.value;
                let privWriteable = widget.domNode.attributes.getNamedItem("data-privilege-writeable")?.value;

                this.registeredPrivilegeNodes.push({
                    element: widget,
                    privilegeVisible: privVisible,
                    privilegeWriteable: privWriteable
                });
            }
        }

        for (let i = 0; i < nodesVis.length; i++) {
            let node = nodesVis[i];

            if (node instanceof HTMLElement) {
                let privVisible = node.attributes.getNamedItem("data-privilege-visible")?.value;
                let privWriteable = node.attributes.getNamedItem("data-privilege-writeable")?.value;

                if (privWriteable) {
                    let index = nodesWri.indexOf(node);
                    if (index > -1) nodesWri.splice(index, 1);
                }

                this.registeredPrivilegeNodes.push({
                    element: node,
                    privilegeVisible: privVisible,
                    privilegeWriteable: privWriteable
                });
            }
        }

        for (let i = 0; i < nodesWri.length; i++) {
            let node = nodesWri[i];

            if (node instanceof HTMLElement) {
                let privWriteable = node.attributes.getNamedItem("data-privilege-writeable")?.value;

                this.registeredPrivilegeNodes.push({
                    element: node,
                    privilegeVisible: null,
                    privilegeWriteable: privWriteable
                });
            }
        }

        // trick: falls die Session (mit den ganzen Privileges drauf!) noch nicht geladen ist, meldet sie sich hier, sobald sie es ist:
        this.hasUserRole("dummy?", lang.hitch(this, this._applyNodePrivileges));

    }
    private _applyNodePrivileges() {
        for (let privilegedEntry of this.registeredPrivilegeNodes) {
            let privW = privilegedEntry.privilegeWriteable ? this.hasUserRole(privilegedEntry.privilegeWriteable) : true;
            let privV = privilegedEntry.privilegeVisible ? this.hasUserRole(privilegedEntry.privilegeVisible) : true;

            if (privilegedEntry.element instanceof HTMLInputElement ||
                privilegedEntry.element instanceof HTMLSelectElement ||
                privilegedEntry.element instanceof HTMLTextAreaElement) {

                //if( !privW) this.UiModder.domClass.add(privilegedEntry.element, "w3-disabled")
                if (!privW) privilegedEntry.element.disabled = true;

                this.hideNode(privilegedEntry.element, !privV);
            } else if (privilegedEntry.element instanceof _StatefulBase) {
                // Achtung: evtl greifen hier noch andere RO-Regeln, die dürfen nicht einfach überschrieben werden ??
                privilegedEntry.element.setReadonly(!privW || privilegedEntry.element.getReadonly() == true);

                this.hideNode(privilegedEntry.element.domNode, !privV);
            } else if (privilegedEntry.element instanceof _StatefulSection) {

                privilegedEntry.element.setReadonly(!privW);

                this.hideNode(privilegedEntry.element.domNode, !privV);
            }
        }
    }
    private _registerFormInputs(): void {
        let nodesList = dojoQuery("[data-key]", this.domNode);
        let conditionalNodes = dojoQuery("[data-user-role]", this.domNode);
        let conditionalNodes2 = dojoQuery("[data-feature-path]", this.domNode);

        // AUS DER BESCHEUERTEN NODESLIST EIN NORMALES ARRAY MACHEN
        // weil mit dem dummen dojo-Zeug gibts kein push und nix!!
        let nodes = [];
        for (let i = 0; i < nodesList.length; i++) {
            nodes.push(nodesList[i]);
        }

        // Arrays zusammenführen
        for (let i = 0; i < conditionalNodes.length; i++) {
            let condNode = conditionalNodes[i];
            let index = nodes.indexOf(condNode);
            if (index == -1) {
                nodes.push(condNode);
            }
        }
        for (let i = 0; i < conditionalNodes2.length; i++) {
            let condNode = conditionalNodes2[i];
            let index = nodes.indexOf(condNode);
            if (index == -1) {
                nodes.push(condNode);
            }
        }


        this.containerNode = this.domNode;
        let registeredWidgets = this.getChildren(); // achtung, das ist nicht rekursiv!
        this.registeredInputs = [];

        // rekursiv erweitern, indem von jedem Widget dessen Children abgefragt und hinten ins Widget eingefügt werden, während der
        // Zähler hinterherläuft
        let count = 0;
        while (count < registeredWidgets.length) {

            let subWidgets = registeredWidgets[count].getChildren();
            for (let sub of subWidgets) {
                if (registeredWidgets.indexOf(sub) == -1)
                    registeredWidgets.push(sub);
            }

            count++;
        }

        this.registeredWidgets = [];

        for (let widget of registeredWidgets) {
            if (widget.domNode) {

                this.registeredWidgets.push(widget);

                // prüfen ob das Widget eine Statefulsection in der Statefulsection ist, anhand von zwei Properties:
                let widgetIsStatefulSection = !!widget["registeredInputs"] && typeof (widget["fillTemplate"]) == "function";

                if (widgetIsStatefulSection) {
                    // weitere Nodes dieses widgets müssen aus der eigenen Registry rausfliegen
                    let widgetnodes = dojoQuery("[data-key]", widget.domNode);
                    widgetnodes.forEach(node => {
                        let nestedIndex = nodes.indexOf(node);
                        if (nestedIndex != -1) {

                            nodes.splice(nestedIndex, 1);
                        }
                    });
                }

                let index = nodes.indexOf(widget.domNode);

                if (index != -1) {
                    nodes.splice(index, 1); // rauskicken: de übrigbleibenden nodes im zweiten Durchlauf behandeln

                    let neededFeature = widget.domNode.attributes.getNamedItem("data-feature-path")?.value;
                    let neededRole = widget.domNode.attributes.getNamedItem("data-user-role")?.value;
                    // PROBLEM? es kann sein, dass die Usersession zu dem Zeitpunkt noch nicht da ist
                    // dann müsste im Falle einer neededRole der ganze Prozess hier angehalten werden und die Registrierung nach dem Laden fortgesetzt
                    if (neededFeature) {
                        if (!this.hasFeature(neededFeature)) {
                            this.hideNode(widget.domNode);
                            this.disableNode(widget.domNode);
                            if (widgetIsStatefulSection) {
                                (widget as _StatefulSection)._isSectionAvailable = false;
                                (widget as _StatefulSection).setStartupState("disabled");
                                (widget as _StatefulSection).setReadonly(true);
                            }
                            continue;
                            // nicht registrieren, nicht Mandatory - ausblenden und einfach nichts damit machen. ignorieren.
                        }
                    }
                    if (neededRole) {
                        if (!this.hasUserRole(neededRole)) {
                            this.hideNode(widget.domNode);
                            this.disableNode(widget.domNode);
                            if (widgetIsStatefulSection) {
                                (widget as _StatefulSection)._isSectionAvailable = false;
                                (widget as _StatefulSection).setStartupState("disabled");
                                (widget as _StatefulSection).setReadonly(true);
                            }
                            continue;
                            // nicht registrieren, nicht Mandatory - ausblenden und einfach nichts damit machen. ignorieren.
                        }
                    }

                    let key = widget.domNode.attributes.getNamedItem("data-key")?.value;

                    if (key) {
                        let isComplexAttribute = key?.indexOf(".") != -1;

                        let reg: RegisteredInput = {
                            input: widget,
                            node: widget.domNode,
                            initialReadonlyState: false,
                            dataKey: key,
                            isComplexAttribute: isComplexAttribute,
                            type: "?"
                        };


                        let strMandatory = widget.domNode.attributes.getNamedItem("data-mandatory")?.value;
                        if (strMandatory) {
                            let isMandatory = false;
                            strMandatory === "true" ? isMandatory = true : isMandatory = false;
                            reg.mandatory = isMandatory;
                        }

                        this.registeredInputs.push(reg);


                        if (!isComplexAttribute) {
                            if (widget instanceof _StatefulBase) {
                                /* das widget ist auch mit data-key versehen und kann von uns benutzt werden */
                                //console.debug(this.id + " register " + widget.id, key, widget.getValue());
                                widget.on("change", lang.hitch(this, this._onChange_WidgetValue_StatefulBase, widget, reg));
                                reg.initialReadonlyState = widget.getReadonly();
                                reg.type = "statefulBase";

                                if (reg.mandatory === true || reg.mandatory === false) {
                                    widget.setMandatory(reg.mandatory);
                                }
                            } else if (widgetIsStatefulSection) {
                                //if (widget.isReadonly != true) {
                                widget.on("change", lang.hitch(this, this._onChange_WidgetValue_StatefulSection, widget, reg));
                                reg.type = "statefulSection";
                                //    }
                            }
                        } else { // bei komplexen Attributen keine Änderungen zulassen!
                            if (widget instanceof _StatefulBase) {
                                /* das widget ist auch mit data-key versehen und kann von uns benutzt werden */
                                widget.setReadonly(true);
                                reg.initialReadonlyState = true;
                                reg.type = "statefulBase";

                            } else if (widget instanceof _StatefulSection) {
                                //widget.setReadonly(true);
                                reg.type = "statefulSection";
                            }

                        }

                        // bei Multilang noch Initialisierung hinterherwerfen:
                        if (reg.input?.["declaredClass"] == "MultiLangEdit") {
                            let complexPathPrefix = this.getComplexAttributePathToDataKey(reg.dataKey);

                            let multireg = reg as RegisteredMultilangInput;

                            // pfad ist zb ref_x.ref_y.description
                            // multilang muss zusätzlich hier abgelegt werden: ref_x.ref_y.__multilang.description
                            let parts = reg.dataKey.split(".");
                            let newPath = "";
                            if (parts.length > 1) {
                                newPath = this.getComplexAttributePathToDataKey(reg.dataKey) + ".";
                            }
                            newPath += "__multilang." + this.getNonComplexAttributeOfDataKey(reg.dataKey);

                            multireg.multilangDatakey = newPath;
                            reg.type = "multilang";

                            reg.input.setAttributeName(this.getNonComplexAttributeOfDataKey(reg.dataKey));
                            reg.input.setValue("");

                            // außerdem weitere data-keys registrieren??
                            let multilangValue = reg.input.getValue();
                            let fakeEntity = {};
                            reg.input.getValueOntoEntity(fakeEntity);
                            this._parseDataPaths(fakeEntity, complexPathPrefix ? complexPathPrefix.split(".") : []);
                            if (complexPathPrefix) complexPathPrefix += ".";

                            // currentdata unbedingt erweitern auf dieses Property!
                            this.translateComplexPath(this.currentData, complexPathPrefix + newPath)

                            //this.setAttributeValue(complexPathPrefix + "__multilang." + this.getNonComplexAttributeOfDataKey(reg.dataKey), multilangValue, true);
                        }
                    }
                }
            }
        }

        for (let i = 0; i < nodes.length; i++) {
            let node = nodes[i];
            if (node instanceof HTMLInputElement ||
                node instanceof HTMLSelectElement ||
                node instanceof HTMLTextAreaElement) {

                let neededFeature = node.attributes.getNamedItem("data-feature-path")?.value;
                let neededRole = node.attributes.getNamedItem("data-user-role")?.value;

                if (neededFeature) {
                    if (!this.hasFeature(neededFeature)) {
                        this.disableNode(node);
                        this.hideNode(node);
                        continue;
                        // nicht registrieren, nicht Mandatory - ausblenden und einfach nichts damit machen. ignorieren.
                    }
                }
                if (neededRole) {
                    if (!this.hasUserRole(neededRole)) {
                        this.disableNode(node);
                        this.hideNode(node);
                        continue;
                        // nicht registrieren, nicht Mandatory - ausblenden und einfach nichts damit machen. ignorieren.
                    }
                }
 
                let key = node.attributes.getNamedItem("data-key")?.value

                if (key) {
                    let isComplexAttribute = key?.indexOf(".") != -1;

                    let isMandatory = node.attributes.getNamedItem("data-mandatory")?.value === "true";

                    let reg: RegisteredInput = {
                        input: node,
                        node: node,
                        initialReadonlyState: node.disabled ||
                            this.UiModder.domClass.contains(node, "w3-disabled") ||
                            this.UiModder.domClass.contains(node, "disabled"),
                        dataKey: key,
                        isComplexAttribute: isComplexAttribute,
                        mandatory: isMandatory,
                        type: "htmlInput"
                    };

                    this.registeredInputs.push(reg);

                    on(node, "change", lang.hitch(this, this._onChange_WidgetValue_NativeHTML, node, reg));
                }
            }
        };
        window.webPrivilegeManager

        // 3. Durchlauf: sonstiges was statische Labels, divs, paragraphs sind
        for (let i = 0; i < nodes.length; i++) {
            let node = nodes[i];

            if (node instanceof HTMLDivElement ||
                node instanceof HTMLSpanElement ||
                node instanceof HTMLPreElement ||
                node instanceof HTMLHeadingElement ||
                node instanceof HTMLParagraphElement ||
                node instanceof HTMLLabelElement) {

                let neededFeature = node.attributes.getNamedItem("data-feature-path")?.value;
                let neededRole = node.attributes.getNamedItem("data-user-role")?.value;

                if (neededFeature) {
                    if (!this.hasFeature(neededFeature)) {
                        this.disableNode(node);
                        this.hideNode(node);
                        continue;
                        // nicht registrieren, nicht Mandatory - ausblenden und einfach nichts damit machen. ignorieren.
                    }
                }
                if (neededRole) {
                    if (!this.hasUserRole(neededRole)) {
                        this.disableNode(node);
                        this.hideNode(node);
                        continue;
                        // nicht registrieren, nicht Mandatory - ausblenden und einfach nichts damit machen. ignorieren.
                    }
                }

                let key = node.attributes.getNamedItem("data-key")?.value

                if (key) {
                    let isComplexAttribute = key?.indexOf(".") != -1;

                    let reg: RegisteredInput = {
                        input: null,
                        node: node,
                        initialReadonlyState: true,
                        dataKey: key,
                        isComplexAttribute: isComplexAttribute,
                        type: "static"
                    };

                    let dateFormat = node.attributes.getNamedItem("data-date-format");
                    if (dateFormat) {
                        reg.format = dateFormat.value;
                    }
                    let format = node.attributes.getNamedItem("data-format");
                    if (format) {
                        reg.format = format.value;
                    }

                    this.registeredInputs.push(reg);
                }
            }
        };

        if (this._readOnlySection != "initial") {
            this.setReadonly(this._readOnlySection);
        }

        //console.debug(this.id + " registered inputs", this.registeredInputs);
    }

    private _processPlacement() {

        let nodesWithSectionName = dojoQuery("[data-section-name]", this.domNode);

        this.registeredSectionNames = [];

        for (let widget of this.registeredWidgets) {

            let nodeIndex = nodesWithSectionName.indexOf(widget.domNode);

            if (nodeIndex != -1) {
                nodesWithSectionName.splice(nodeIndex, 1);

                let nodename = widget.domNode.attributes.getNamedItem("data-section-name").value;

                this.registeredSectionNames.push({
                    element: widget,
                    name: nodename
                })
            }
        }

        for (let i = 0; i < nodesWithSectionName.length; i++){
            let node = nodesWithSectionName[i];

            if (node instanceof HTMLElement) {

                this.registeredSectionNames.push({
                    element: node,
                    name: node.attributes.getNamedItem("data-section-name").value
                });
            }
        };

        let nodesWithTarget = dojoQuery("[data-target-container]", this.domNode);

        for (let i = 0; i < nodesWithTarget.length; i++) {
            let node = nodesWithTarget[i];

            if (node instanceof HTMLElement) {
                let path = node.attributes.getNamedItem("data-target-container").value;
                if (path) {
                    let pathParts = path.split(".");

                    if (pathParts.length == 2) {
                        // 1. Teil = Section
                        // 2. Teil = attachpoint
                        let section = pathParts[0];
                        let targetContainerName = pathParts[1];
                        //this._attachPoints
                        if (section && targetContainerName) {
                            console.log(section, targetContainerName);
                            for (let registeredSection of this.registeredSectionNames) {
                                if (registeredSection.name == section) {
                                    if (registeredSection.element instanceof _WidgetBase) {
                                        // falls es ein Widget ist, sollte das target als Attachpoint eingetragen sein:
                                        if (((registeredSection.element)as any)._attachPoints.indexOf(targetContainerName) != -1) {
                                            this.UiModder.domConstruct.place(node, registeredSection.element[targetContainerName]);
                                        } else {
                                            // kein Attachpoint im Widget? dann vielleicht auch ein data-section-name?
                                            var containers = dojoQuery("[data-section-name]", registeredSection.element["domNode"]);
                                        }
                                    } else if (registeredSection.element instanceof HTMLElement) {
                                        // dann muss es irgendwo in dem Element ein direktes oder indirektes Child geben mit dem Containername
                                        var containers = dojoQuery("[data-section-name]", registeredSection.element);
                                    }

                                    if (containers?.length) {
                                        for (let x = 0; x < containers.length; x++) {
                                            let childNode = containers[x];

                                            if (childNode instanceof HTMLElement) {
                                                let containerName = childNode.attributes.getNamedItem("data-section-name").value;

                                                if (containerName && containerName == targetContainerName) {
                                                    this.UiModder.domConstruct.place(node, childNode);
                                                    //console.log("place", node, childNode );
                                                    break;
                                                }
                                            }
                                        }
                                    }
                                    break;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    private _registerConditionalNodes() {

        let nodesWithHideCondition = dojoQuery("[data-hideif-empty]", this.domNode);
        let nodesWithShowCondition = dojoQuery("[data-showif-value]", this.domNode);

        let nodesWithCondition = [];

        // Arrays zusammenführen. weil Listen einfach partout kein concat und kein push und nichts können, hier eine schöne ausprogrammierte Variante. hmpf.
        for (let i = 0; i < nodesWithShowCondition.length; i++) {
            let node = nodesWithShowCondition[i];
            if (nodesWithCondition.indexOf(node) == -1) {
                nodesWithCondition.push(node);
            }
        }
        for (let i = 0; i < nodesWithHideCondition.length; i++) {
            let node = nodesWithHideCondition[i];
            if (nodesWithCondition.indexOf(node) == -1) {
                nodesWithCondition.push(node);
            }
        }

        this.registeredConditionalNodes = [];

        for (let widget of this.registeredWidgets) {

            let nodeIndex = nodesWithCondition.indexOf(widget.domNode);

            if (nodeIndex != -1) {
                nodesWithCondition.splice(nodeIndex, 1);
            }
        }

        for (let i = 0; i < nodesWithCondition.length; i++) {
            let node = nodesWithCondition[i];

            if (node instanceof HTMLElement) {

                let value = node.attributes.getNamedItem("data-hideif-empty")?.value;


                if (value) {
                    let parts = value.split("|");
                    let targetValue = null;
                    if (parts.length >= 1) {
                        value = parts[0];
                        if (parts.length > 1)
                        targetValue = parts[1];
                    }
                    let isReverse = false;

                    if (value.substr(0, 1) == "!") {
                        isReverse = true;
                        value = value.substr(1, value.length - 1);
                    } else if (targetValue && targetValue.substr(0, 1) == "!") {
                        isReverse = true;
                        targetValue = targetValue.substr(1, targetValue.length - 1);
                    }

                    this.registeredConditionalNodes.push({
                        node: node,
                        dataKey: value,
                        dataValue: targetValue,
                        reverse: isReverse,
                        action: "hideIfEmpty"
                    });
                }
                value = node.attributes.getNamedItem("data-showif-value")?.value;

                if (value) {

                    let parts = value.split("|");
                    let targetValue = null;
                    if (parts.length >= 1) {
                        value = parts[0];
                        if (parts.length > 1)
                        targetValue = parts[1];
                    }
                    let isReverse = false;

                    if (value.substr(0, 1) == "!") {
                        isReverse = true;
                        value = value.substr(1, value.length - 1);
                    } else if (targetValue && targetValue.substr(0, 1) == "!") {
                        isReverse = true;
                        targetValue = targetValue.substr(1, targetValue.length - 1);
                    }

                    this.registeredConditionalNodes.push({
                        node: node,
                        dataKey: value,
                        dataValue: targetValue,
                        reverse: isReverse,
                        action: "showIfValue"
                    });
                }
            }
        };
    }

    _intervalTest: any;

    postCreate(): void {
        let self: any = this;
        super.postCreate.apply(this, arguments as any);
        this.dataKeys = [];
        this.dataValues = [];

        this.changedData = {};
        this.currentData = {};
        this._onChangedRegistry = [];


        this.registeredConditionalNodes = [];
        this.registeredInputs = [];
        this.registeredPrivilegeNodes = [];
        this._registerFormInputs();

        this._setDataCyAttribute();

        //console.debug(this.id + " registering forminputs done!", this.registeredInputs);

        this._registerPrivilegedElements();
        this._registerConditionalNodes();

        this._processPlacement();


        setTimeout(lang.hitch(this, function () {
            if (this.startupState == "initial") {
                alert(this.id + " still not ready or loading.\r\nMust use setStartupState in lifecycle of widget!");
            }
        }), 200);

        /*this._intervalTest = setInterval(lang.hitch(this, function () {
            console.log(this.id, this.startupState);

            if (this.startupState == "ready") {
                clearInterval(this._intervalTest);

            }
        }), 20);*/
    }

    private __isDojoStartupDone: boolean;

    startup(): void {
        if (this.__isDojoStartupDone === true) return;

        //if (this.startupState != "ready") return;

        // das normale startup (selects und widgets starten los - ggf mit Requests!) nur ausführen, wenn ich als Section
        // nicht von einem Parent als "nicht verfügbar" (Rolle oder Feature) geflaggt wurde!
        if (this._isSectionAvailable) {
            let self: any = this;
            super.startup.apply(this, arguments as any);

            this.__isDojoStartupDone = true;
        }
        //console.debug(this.id + " STARTUP DONE");
        //this.startupState = "ready";

    }

    private _setDataCyAttribute() {
        //debugger;
        for (let regInput of this.registeredInputs) {
            const inputElement = this._findSelfOrNextInputElement(regInput.node);
            if (inputElement) {
                inputElement.setAttribute("data-cy", this.dojoAttachPoint + ":" + this._findDataKey(inputElement));
            }
        }
    }
    
    //find the next node down with type HTMLInputElement, HTMLTextAreaElement,HTMLSelectElement or self
    private _findSelfOrNextInputElement(node: Node | null): HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null {
            const start = node instanceof Element ? node : node?.parentElement ?? null;
            if (!start) return null;

            if (
                start instanceof HTMLInputElement ||
                start instanceof HTMLTextAreaElement ||
                start instanceof HTMLSelectElement
            ) {
                return start;
            }

            const match = start.querySelector('input, textarea, select');

            if (
                match instanceof HTMLInputElement ||
                match instanceof HTMLTextAreaElement ||
                match instanceof HTMLSelectElement
            ) {
                return match;
            }

            return null;
    }

    //find the data-key from the next node up or self
    private _findDataKey(node: Node | null): string {
        //debugger;
            const start = node instanceof Element ? node : node?.parentElement ?? null;
            const dataKey = start?.closest('[data-key]');
            return dataKey?.getAttribute("data-key") ?? "";
    }
}
;
Object.assign(__C.prototype, {
  formData: null,
  dirty: false,
  valid: true,
  sourceData: null,
  currentData: null,
  isDataDeleted: false,
  changedData: null,
  _undirtyData: null,
  _readOnlySection: "initial",
  _isSectionAvailable: true,
  entityId: null,
  displayname: null,
  forceCheckDirty: false,
  valueProperty: "ID",
  startupState: "initial",
  _onChangedRegistry: null,
  __isDojoStartupDone: false,
  declaredClass: '_StatefulSection',
});
  return __C;
}
Object.assign(_StatefulSection, { __mdsMixinFactory: true, __mdsSpineBases: [_WidgetBaseSP] });
